import { notification } from "antd";
import axios, { AxiosError } from "axios";
import { translation } from "../i18n/translationConstant";
import { UserLoginSuccessResponse } from "../Models/Response/Authentication/Otp";
import { pauseLoader, startLoader } from "../redux/actions";
import store from "../redux/store";
import { getAccessToken } from "../Services/AuthenticationServices/AuthenticationServices";
import { clearLoginSession } from "../Services/LocalStorageServices";
import * as tokenStore from "../Services/TokenStore";
import {
  encryptRequest,
  isEncryptedRequest,
  isWebRoute,
  KEY_REFRESH_ERROR_CODES,
  loadPublicKey,
  WEB_ROUTES,
} from "../Services/WebEncryption";
import { QueryParams, Routes } from "./Constants/routes";
import { apiTimeout } from "./Constants/apiConstants";

const REFRESH_URL = WEB_ROUTES.refreshToken;

// Login steps whose 401 means wrong credentials, not an expired session.
const LOGIN_URLS: string[] = [
  WEB_ROUTES.verifyOtp,
  WEB_ROUTES.login,
  WEB_ROUTES.sendOtp,
  WEB_ROUTES.signupVerify,
  WEB_ROUTES.loginWithPin,
];

// Shown for any web-encryption failure; never exposes error codes or crypto detail.
const notifySecureChannelError = () =>
  notification.error({
    message: "Secure connection unavailable",
    description: "Your request was not sent. Please try again later.",
    duration: 3,
  });

const interceptor = (history: any, t: any) => {
  // One refresh at a time: concurrent 401s wait for the same request.
  let refreshInFlight: Promise<string> | null = null;

  const expireSession = () => {
    const hadSession = tokenStore.hasSession() || !!tokenStore.getRefreshToken();
    clearLoginSession();
    if (hadSession) {
      notification.error({
        message: "Session Timeout",
        description: "",
        duration: 3,
      });
    }
    if (history.location.pathname !== Routes.login) {
      history.push({
        pathname: Routes.login,
        search: `?${QueryParams.redirect}=${history.location.pathname}`,
        state: { search: history.location.search },
      });
    }
  };

  const refreshAccessToken = () => {
    if (!refreshInFlight) {
      const refreshToken = tokenStore.getRefreshToken();
      refreshInFlight = (
        refreshToken
          ? getAccessToken(refreshToken)
          : Promise.reject(new Error("No refresh token in memory"))
      )
        .then((res) => res.data)
        .then((res: UserLoginSuccessResponse) => {
          if (!res.access_token) {
            throw new Error("Refresh response has no access token");
          }
          tokenStore.setAccessToken(res.access_token);
          // Keycloak may rotate the refresh token; keep the newest one.
          if (res.refresh_token) tokenStore.setRefreshToken(res.refresh_token);
          return res.access_token;
        })
        .catch((refreshTokenError) => {
          expireSession();
          throw refreshTokenError;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  };

  axios.defaults.baseURL =
    localStorage.getItem("isV1") === "true"
      ? process.env.REACT_APP_BASE_URL_V1
      : process.env.REACT_APP_BASE_URL;
  axios.interceptors.request.use(
    (request) => {
      store.dispatch(startLoader());
      if (request.data === undefined) {
        request.data = {};
      }
      request.headers = {
        ...request.headers,
        "Content-Type": "application/json",
      };

      const accessToken = tokenStore.getAccessToken();
      if (
        accessToken &&
        !request.url?.includes("public") &&
        !request.url?.includes("/User/forgetPassword")
      ) {
        request.headers = {
          ...request.headers,
          Authorization: `Bearer ${accessToken}`,
        };
      }

      request.timeout = apiTimeout;

      // Encrypted web routes: the body is replaced by the envelope, or nothing is sent.
      return isWebRoute(request.url) ? encryptRequest(request) : request;
    },
    (error) => {
      // Do something with request error
      return Promise.reject(error);
    }
  );

  // Add a response interceptor
  axios.interceptors.response.use(
    // tslint:disable-next-line: only-arrow-functions
    function (response) {
      store.dispatch(pauseLoader());
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      return response;
    },
    // tslint:disable-next-line: only-arrow-functions
    function (error: AxiosError) {
      store.dispatch(pauseLoader());
      // Encryption failed in the browser: the request was never sent.
      if ((error as any).isWebEncryptionError) {
        notifySecureChannelError();
        return Promise.reject(error);
      }
      const originalRequest: any = error.config;
      const errorCode = error.response?.data?.errorCode;
      if (
        isEncryptedRequest(originalRequest) &&
        typeof errorCode === "string" &&
        errorCode.startsWith("WEB_ENC_")
      ) {
        // Rejected before decryption: refetch the key and re-encrypt once (never the same envelope).
        if (KEY_REFRESH_ERROR_CODES.includes(errorCode) && !originalRequest._webEncKeyRetried) {
          originalRequest._webEncKeyRetried = true;
          return loadPublicKey(true)
            .then(() => axios(originalRequest))
            .catch((retryError) => {
              if (retryError?.isWebEncryptionError) notifySecureChannelError();
              throw retryError;
            });
        }
        notifySecureChannelError();
        return Promise.reject(error);
      }
      if (originalRequest.url === REFRESH_URL) {
        return Promise.reject(error);
      }
      if (error.response?.status === 401) {
        if (!LOGIN_URLS.includes(originalRequest.url)) {
          // Each request is retried at most once, so a rejected new token cannot loop.
          if (originalRequest._authRetried) {
            expireSession();
            return Promise.reject(error);
          }
          originalRequest._authRetried = true;

          // The token was already refreshed after this request was sent: just retry.
          const current = tokenStore.getAccessToken();
          const sent = originalRequest.headers?.Authorization;
          if (current && sent && sent !== `Bearer ${current}`) {
            return axios(originalRequest);
          }

          return refreshAccessToken().then(() => axios(originalRequest));
        } else {
          notification.error({
            message: error.response?.data.errorCode
              ? error.response?.data.errorCode
              : t(translation.lblUnAuthorized),
            description: "",
            duration: 3,
          });
        }
      } else if (error.code === "ECONNABORTED") {
        notification.error({
          message: "Server Timeout",
          duration: 3,
        });
      } else {
        // notification.error({
        //   message: error.response?.data.errorCode
        //     ? error.response?.data.errorCode
        //     : "Your request cannot be processed at the moment. Please try again later.",
        //   description: error.response?.data.errorMsg
        //     ? error.response?.data.errorMsg
        //     : "",
        //   duration: 3,
        // });

        if (error.response?.data?.errorCode) {
          notification.error({
            message: error.response.data.errorCode,
            description: error.response.data.errorMsg || "",
            duration: 3,
          });
        }
        if (error.response?.data.redirectUrl) {
          window.location.replace(error.response?.data.redirectUrl);
        }
      }

      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      return Promise.reject(error);
    }
  );
};
export default interceptor;
